import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, ChevronDown, ChevronUp, Send, User, Brain, ExternalLink, FileText } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './Chatbot.css';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://chatbot.gallup.com.pk/api/v1';
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const [lastDataUpdate, setLastDataUpdate] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch initial data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/`);
        const timestamp = response.data.timestamp;
        setLastDataUpdate(timestamp);

        // Format the date nicely
        const date = new Date(timestamp);
        const formattedDate = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        // Set welcome message with last update date
        setMessages([{
          id: 1,
          text: `Hi! I'm Gallup's Research Buddy; here to assist you as you navigate through our research. Are you looking for something specific? Let me know and I'll source it for you. Otherwise, ask me any questions you have on economic trends, policy, media or current affairs.\n\n`,
          isBot: true,
          timestamp: new Date()
        }]);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        // Set default welcome message if fetch fails
        setMessages([{
          id: 1,
          text: "Hi! I'm Gallup's Research Buddy; here to assist you as you navigate through our research. Are you looking for something specific? Let me know and I'll source it for you. Otherwise, ask me any questions you have on economic trends, policy, media or current affairs.",
          isBot: true,
          timestamp: new Date()
        }]);
      }
    };

    fetchInitialData();
  }, []);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const question = inputValue;
    setInputValue('');
    setIsTyping(true);
    setError(null);

    try {
      // Call the backend API
      const response = await axios.post(`${API_BASE_URL}/query`, {
        question: question,
        session_id: sessionId
      }, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Extract response data
      const { answer, session_id, sources, metadata } = response.data;

      // Update session ID if not set
      if (!sessionId && session_id) {
        setSessionId(session_id);
      }

      // Create bot response message
      const botMessage = {
        id: messages.length + 2,
        text: answer,
        isBot: true,
        timestamp: new Date(),
        sources: sources || [],
        metadata: metadata || {}
      };

      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);

    } catch (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);

      let errorMessage = "Sorry, I encountered an error processing your request.";
      let isTokenLimitError = false;

      if (error.response) {
        // Server responded with error
        if (error.response.status === 429) {
          // Check if it's a token/quota limit error (either monthly limit or OpenAI rate limit)
          const detail = error.response.data?.detail || "";
          if (detail.includes("Monthly token limit") || detail.includes("token limit exceeded") || detail.includes("OpenAI API rate limit")) {
            errorMessage = "Hello, I apologise for not being able to assist you at this moment. Please leave us a message here: https://gallup.com.pk/contact , and we will get back to you.";
            isTokenLimitError = true;
            setIsDisabled(true);
          } else {
            errorMessage = "You're sending requests too quickly. Please wait a moment and try again.";
          }
        } else if (error.response.status === 503) {
          errorMessage = "Service is not ready yet. Please try again in a moment.";
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = "Cannot connect to the server. Please check if the backend is running.";
      }

      setError(errorMessage);

      const errorBotMessage = {
        id: messages.length + 2,
        text: errorMessage,
        isBot: true,
        timestamp: new Date(),
        isError: !isTokenLimitError
      };

      setMessages(prev => [...prev, errorBotMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleSources = (messageId) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
    <>
      {/* Chat Window */}
      <div className={`chatbot-window ${isOpen ? 'open' : 'closed'}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-content">
            <div className="chatbot-avatar">
              <Brain size={24} />
            </div>
            <div>
              <h3 className="chatbot-title">Gallup's Research Buddy</h3>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="chatbot-header-button"
            aria-label="Close chat"
          >
            <ChevronDown size={20} />
          </button>
        </div>

        {/* Messages Area */}
        <div className="chatbot-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message-wrapper ${message.isBot ? 'bot' : 'user'}`}
            >
              <div className={`message ${message.isBot ? (message.isError ? 'error' : 'bot') : 'user'}`}>
                {message.isBot && (
                  <div className="message-header">
                    <User size={16} />
                    <span className="message-label">Gallup's Research Buddy</span>
                  </div>
                )}
                <div className="message-text">
                  <ReactMarkdown
                    source={message.text}
                    linkTarget="_blank"
                  />
                </div>

                {/* Display sources if available */}
                {message.sources && message.sources.length > 0 && (
                  <div className="sources-section">
                    <div
                      className="sources-header"
                      onClick={() => toggleSources(message.id)}
                    >
                      <div className="sources-header-content">
                        <FileText size={16} color="#4f46e5" />
                        <p className="sources-title">
                          Sources & Citations ({message.sources.length})
                        </p>
                      </div>
                      {expandedSources[message.id] ? (
                        <ChevronUp size={16} color="#6b7280" />
                      ) : (
                        <ChevronDown size={16} color="#6b7280" />
                      )}
                    </div>

                    {expandedSources[message.id] && (
                      <div className="sources-list">
                        {message.sources.map((source, idx) => (
                          <div key={idx} className="source-item">
                            <div className="source-content">
                              <div className="source-icon">
                                <ExternalLink size={12} color="#4f46e5" />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {((source.url && source.url.trim() !== '') || source.post_id) ? (
                                  <a
                                    href={(source.url && source.url.trim() !== '') ? source.url : `https://www.gallup.com.pk/post/${String(source.post_id).split('_').pop()}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="source-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    {source.title || `Source ${idx + 1}`}
                                  </a>
                                ) : (
                                  <span className="source-link" style={{ color: '#374151' }}>
                                    {source.title || `Source ${idx + 1}`}
                                  </span>
                                )}
                                {source.content && (
                                  <p className="source-excerpt">
                                    {source.content.substring(0, 100)}...
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <span className="message-timestamp">
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-bubble">
                <div className="typing-dots">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="chatbot-input-area">
          {isDisabled ? (
            <div className="chatbot-disabled-message">
              Hello, I apologise for not being able to assist you at this moment. Please leave us a message here: https://gallup.com.pk/contact , and we will get back to you.
            </div>
          ) : (
            <div className="chatbot-input-wrapper">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about surveys, trends, research..."
                className="chatbot-input"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="chatbot-send-button"
                aria-label="Send message"
              >
                <Send size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`chatbot-button ${isOpen ? 'close' : 'pulse'}`}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        title="Chat with Gallup's Research Buddy"
      >
        {isOpen ? (
          <X size={28} color="white" />
        ) : (
          <MessageCircle size={28} color="white" />
        )}
      </button>
    </>
  );
};

export default Chatbot;
